import { useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { ExternalLink, Eye, Loader2 } from 'lucide-react'
import {
  getAssociationSourceKey,
  getAssociationSourceLabel,
} from '@/pages/quizSetAssociationSource'
import {
  BIGGO_MIGRATION_STATUS_OPTIONS,
  getBiggoMigrationStatus,
} from '@/pages/quizGroupBiggoMigrationStatus'
import {
  countQuizItemTypes,
  type LessonAssociationRow,
} from '@/pages/quizSetAssociationHierarchy'
import type { BiggoMigrationStatus } from '@/types/quizSet'
import { QuizGroupPreviewModal } from './QuizGroupPreviewModal'

const LEGACY_ADMIN_QUIZ_URL = 'https://adm.quebon.tv/quiz'

export function getLegacyAdminQuizEditUrl(quizId: string): string {
  const encodedQuizId = encodeURIComponent(quizId)
  return `${LEGACY_ADMIN_QUIZ_URL}?type=&category=id&by=modified&findText=${encodedQuizId}&editId=${encodedQuizId}`
}

interface LessonAssociationTableProps {
  rows: LessonAssociationRow[]
  updatingAssociationId?: number | null
  updatingBiggoStatusAssociationId?: number | null
  onActiveChange?: (associationId: number, isActive: boolean) => void
  onBiggoStatusChange?: (associationId: number, status: BiggoMigrationStatus) => void
}

export function LessonAssociationTable({
  rows,
  updatingAssociationId = null,
  updatingBiggoStatusAssociationId = null,
  onActiveChange,
  onBiggoStatusChange,
}: LessonAssociationTableProps) {
  const [previewRow, setPreviewRow] = useState<LessonAssociationRow | null>(null)

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-12 text-center">
        <p className="text-sm font-semibold text-slate-600">조회 조건에 해당하는 레슨 연결이 없습니다</p>
        <p className="mt-1 text-xs text-slate-400">출처 필터나 활성 조건을 변경해 보세요.</p>
      </div>
    )
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1580px] text-sm">
            <thead className="bg-slate-50/90 text-left text-xs font-semibold text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3">코스</th>
                <th className="px-4 py-3">모듈</th>
                <th className="px-4 py-3">레슨</th>
                <th className="px-4 py-3">출처 / 그룹</th>
                <th className="w-28 px-3 py-3 text-center">연결 상태</th>
                <th className="w-36 px-3 py-3 text-center">Biggo 검수</th>
                <th className="w-16 px-3 py-3 text-center text-blue-700">Q</th>
                <th className="w-16 px-3 py-3 text-center text-rose-700">S</th>
                <th className="w-16 px-3 py-3 text-center text-amber-700">QQS</th>
                <th className="min-w-[360px] px-4 py-3">문제 ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
            {rows.map((row, index) => {
              const previous = rows[index - 1]
              const courseStarts = previous?.course.id !== row.course.id
              const moduleStarts = courseStarts || previous?.module.id !== row.module.id
              const lessonStarts = moduleStarts || previous?.lesson.id !== row.lesson.id
              const counts = countQuizItemTypes(row.association)
              const source = getAssociationSourceKey(row.association)

              return (
                <tr
                  key={`${row.course.id}-${row.module.id}-${row.lesson.id}-${row.association.id}`}
                  className={`align-top transition-colors hover:bg-slate-50/70 ${courseStarts && index > 0 ? 'border-t-2 border-t-slate-300' : ''}`}
                >
                  <HierarchyCell title={row.course.title} id={row.course.id} visible={courseStarts} />
                  <HierarchyCell title={row.module.title} id={row.module.id} visible={moduleStarts} />
                  <td className="px-4 py-3">
                    {lessonStarts ? (
                      <div className="max-w-64">
                        <p className="font-semibold text-slate-800"><ContentMetadataText text={row.lesson.title} /></p>
                        <p className="mt-0.5 font-mono text-[11px] text-slate-400">content #{row.lesson.id}</p>
                      </div>
                    ) : (
                      <span className="text-slate-300">〃</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex max-w-64 flex-col items-start gap-1">
                      <SourceBadge source={source} />
                      <span className="font-semibold text-slate-700">{row.association.group.name}</span>
                      <span className="font-mono text-[11px] text-slate-400">group #{row.association.group.id}</span>
                      <button
                        type="button"
                        onClick={() => setPreviewRow(row)}
                        className="mt-1 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 transition hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                      >
                        <Eye className="h-3.5 w-3.5" /> 그룹 프리뷰
                      </button>
                    </div>
                  </td>
                  <AssociationActiveControl
                    associationId={row.association.id}
                    isActive={row.association.isActive}
                    isGroupActive={row.association.group.isActive}
                    isPending={updatingAssociationId === row.association.id}
                    onChange={onActiveChange}
                  />
                  <BiggoMigrationStatusControl
                    associationId={row.association.id}
                    status={getBiggoMigrationStatus(row.association)}
                    isPending={updatingBiggoStatusAssociationId === row.association.id}
                    onChange={onBiggoStatusChange}
                  />
                  <TypeCount value={counts.Q} tone="aiq" />
                  <TypeCount value={counts.S} tone="s" />
                  <TypeCount value={counts.QQS} tone="qqs" />
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5">
                      {row.association.group.items.map((item) => (
                        <div
                          key={`${item.sequenceOrder}-${item.type}-${item.quiz.quizId}`}
                          className="flex min-w-0 flex-wrap items-center gap-2"
                        >
                          <span className="inline-flex min-w-10 justify-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-600">
                            {item.type}
                          </span>
                          <span className="font-mono text-xs tabular-nums text-slate-600">{item.quiz.quizId}</span>
                          <a
                            href={getLegacyAdminQuizEditUrl(item.quiz.quizId)}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`문제 ${item.quiz.quizId} Legacy Admin에서 편집`}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                          >
                            편집 <ExternalLink className="h-3 w-3" />
                          </a>
                          {item.quiz.title ? (
                            <span className="max-w-52 truncate text-xs text-slate-400" title={item.quiz.title}>{item.quiz.title}</span>
                          ) : null}
                        </div>
                      ))}
                      {counts.other > 0 ? (
                        <span className="text-xs font-medium text-rose-600">기타 type {counts.other}개 확인 필요</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )
            })}
            </tbody>
          </table>
        </div>
      </div>
      {previewRow ? (
        <QuizGroupPreviewModal row={previewRow} onClose={() => setPreviewRow(null)} />
      ) : null}
    </>
  )
}

function BiggoMigrationStatusControl({
  associationId,
  status,
  isPending,
  onChange,
}: {
  associationId: number
  status: BiggoMigrationStatus
  isPending: boolean
  onChange?: (associationId: number, status: BiggoMigrationStatus) => void
}) {
  return (
    <td className="px-3 py-3 text-center">
      <div className="relative inline-flex items-center">
        <select
          aria-label="Biggo 이관 검수 상태"
          value={status}
          disabled={onChange == null || isPending}
          onChange={(event) => onChange?.(
            associationId,
            event.target.value as BiggoMigrationStatus,
          )}
          className="h-8 min-w-28 rounded-md border border-slate-300 bg-white px-2 pr-7 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:opacity-70"
        >
          {BIGGO_MIGRATION_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {isPending ? (
          <Loader2 className="pointer-events-none absolute right-2 h-3.5 w-3.5 animate-spin text-slate-500" />
        ) : null}
      </div>
    </td>
  )
}

function AssociationActiveControl({
  associationId,
  isActive,
  isGroupActive,
  isPending,
  onChange,
}: {
  associationId: number
  isActive: boolean
  isGroupActive: boolean
  isPending: boolean
  onChange?: (associationId: number, isActive: boolean) => void
}) {
  const isEditable = onChange != null
  return (
    <td className="px-3 py-3 text-center">
      <div className="flex flex-col items-center gap-1.5">
        <button
          type="button"
          role="switch"
          aria-checked={isActive}
          aria-label={`레슨 연결 ${isActive ? '비활성화' : '활성화'}`}
          title="이 레슨과 문제 그룹의 연결 상태만 변경합니다."
          disabled={!isEditable || isPending}
          onClick={() => onChange?.(associationId, !isActive)}
          className={`inline-flex min-w-[72px] items-center justify-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset transition disabled:cursor-not-allowed disabled:opacity-60 ${
            isActive
              ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100'
              : 'bg-slate-100 text-slate-500 ring-slate-300 hover:bg-slate-200'
          }`}
        >
          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {isActive ? '활성' : '비활성'}
        </button>
        {!isGroupActive ? (
          <span
            title="문제 그룹 자체가 비활성 상태입니다."
            className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600 ring-1 ring-inset ring-rose-200"
          >
            그룹 비활성
          </span>
        ) : null}
      </div>
    </td>
  )
}

function HierarchyCell({ title, id, visible }: { title: string; id: number; visible: boolean }) {
  return (
    <td className="px-4 py-3">
      {visible ? (
        <div className="max-w-52">
          <p className="font-medium text-slate-700"><ContentMetadataText text={title} /></p>
          <p className="mt-0.5 font-mono text-[11px] text-slate-400">#{id}</p>
        </div>
      ) : (
        <span className="text-slate-300">〃</span>
      )}
    </td>
  )
}

function TypeCount({ value, tone }: { value: number; tone: 'aiq' | 's' | 'qqs' }) {
  const toneClass = tone === 'aiq'
    ? 'bg-blue-50 text-blue-700 ring-blue-200'
    : tone === 's'
      ? 'bg-rose-50 text-rose-700 ring-rose-200'
      : 'bg-amber-50 text-amber-700 ring-amber-200'
  return (
    <td className="px-3 py-3 text-center">
      <span className={`inline-flex min-w-8 justify-center rounded-md px-2 py-1 font-bold tabular-nums ring-1 ring-inset ${toneClass}`}>
        {value}
      </span>
    </td>
  )
}

function SourceBadge({ source }: { source: string }) {
  const isAiq = source === 'qb_aiq'
  const isLegacy = source === 'legacy_ttaedaeggae' || source === 'legacy_aiq'
  const toneClass = isAiq
    ? 'bg-blue-50 text-blue-700 ring-blue-200'
    : isLegacy
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : 'bg-slate-100 text-slate-600 ring-slate-200'
  return (
    <span
      title={source}
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${toneClass}`}
    >
      {getAssociationSourceLabel(source)}
      <span className="ml-1 font-mono font-normal opacity-65">{source}</span>
    </span>
  )
}
